<template>
  <section ref="rootRef" class="staff-review-rail">
    <div :class="['review-pane', review.showStaffPickIcon ? 'has-staff-pick' : '']" :style="paneStyle">
      <div class="review-header">
        <img
          v-if="review.authorImageUrl"
          class="author-image lazy"
          :data-src="review.authorImageUrl"
          alt=""
        />
        <IconStar v-for="star of stars" :key="star" :class="['star', isBackgroundPink ? 'white' : '']" />
      </div>

      <h3 class="review-title" v-if="reviewTitle">{{ reviewTitle }}</h3>
      <p class="review-body" v-if="reviewBody">{{ reviewBody }}</p>

      <div v-if="review.showStaffPickIcon" :class="['staff-pick-container', isBackgroundPink ? 'white' : '']">
        <p class="staff-pick-label">{{ $t('misc.staffPick') }}</p>
        <IconStaffPick  class="staff-pick" />
      </div>
      
    </div>

    <div class="image-wrapper">
      <TG4Image
        :image="rail.image"
        aspect="square"
        :alt="reviewTitle"
        class="review-image"
        :id="itemId(0)"
        tabindex="0"
        v-focus
        @up="handleUp"
        @down="handleDown"
        @left="handleLeftFallback"
        v-press="handlePress"
      />

    </div>
  </section>
</template>

<script setup lang="ts">

import { ref, computed, onMounted, getCurrentInstance } from 'vue';
import { RailResponse, RailStaffReview } from '@/types/cms';
import { Language } from '@/enums/language';
import { useStore } from '@/composables/useStore';
import { useRailNavigation } from '@/composables/useRailNavigation';
import { useCMSItemNavigation } from '@/composables/useCMSItemNavigation';
import { IconStar, IconStaffPick } from '@/components/icons';
import TG4Image from '@/components/TG4Image.vue';

/** `rating` is unbounded upstream, so it cannot be trusted to be a star count. */
const MAX_STARS = 5;

const props = defineProps<{
  rail: RailResponse;
  railIndex: number;
  railDidNavigateDown?: (railIndex: number) => void;
}>();

const { itemId, handleUp, handleDown, handleLeftFallback } = useRailNavigation(props);
const { openItem } = useCMSItemNavigation();

const { useGetter } = useStore();
const currentLanguage = useGetter<string>('currentLanguage');

const isIrish = computed(() => currentLanguage.value === Language.Irish);

const EMPTY_REVIEW: RailStaffReview = {
  titleEn: '',
  titleGa: '',
  reviewEn: '',
  reviewGa: '',
  rating: null,
  authorImageUrl: null,
  showStaffPickIcon: false,
};

const review = computed(() => props.rail.staffReview ?? EMPTY_REVIEW);

const reviewTitle = computed(() =>
  (isIrish.value ? review.value.titleGa : review.value.titleEn) || review.value.titleEn
);

const reviewBody = computed(() =>
  (isIrish.value ? review.value.reviewGa : review.value.reviewEn) || review.value.reviewEn
);

const stars = computed(() => {
  const rating = review.value.rating ?? 0;
  const count = Math.min(MAX_STARS, Math.max(0, Math.floor(rating)));
  return Array.from({ length: count }, (_unused, index) => index);
});

const paneStyle = computed(() => ({
  'background-color': props.rail.backgroundColor ?? undefined,
  color: props.rail.textColor ?? undefined,
}));

const isBackgroundPink = computed(() => props.rail.backgroundColor === '#FF126C');

const handlePress = () => {
  const item = props.rail.items[0];
  if (item) openItem(item);
};

const rootRef = ref<HTMLElement | null>(null);
const lazyLoad = getCurrentInstance()?.proxy?.$lazyLoad;

const scanForLazyImages = () => {
  setTimeout(() => {
    if (rootRef.value) lazyLoad?.(rootRef.value);
  }, 200);
};

onMounted(scanForLazyImages);

</script>

<style scoped lang="scss">
.staff-review-rail {
  position: relative;
  display: flex;
  align-items: stretch;
  height: $staff-review-height;
  margin-bottom: 40px;
}

.review-pane {
  position: relative;
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 0 40px;
  text-align: left;
  background-color: $color-background-grey;
  @include text-white;

  // A backstop: the copy is clamped to fit, but a long word or a bigger system
  // font should crop rather than spill into the rails above and below.
  overflow: hidden;
}

// The badge is pinned to the corner, so its band has to come out of the
// centred content box or the last line of the review ends up underneath it.
.has-staff-pick {
  padding-bottom: 80px;
}

.review-header {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
}

.author-image {
  width: 72px;
  height: 72px;
  margin-right: 16px;
  object-fit: cover;
  border-radius: 50%;
}

.star {
  width: 50px;
  height: 50px;

  color: $color-pink;
  &.white {
    color: $color-off-white;
  }
}

.review-title {
  margin: 0 0 12px 0;
  @include text-title;
  @include line-clamp(2);
}

.review-body {
  margin: 0;
  @include text-body;
  @include line-clamp(4);
}


.staff-pick {
  width: 104px;
  height: 40px;

}

.staff-pick-container {
  position: absolute;
  left: 40px;
  bottom: 24px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  color: $color-pink;
  &.white {
    color: $color-slate;
  }
}

.staff-pick-label {
  font-family: $font-tg4;
  font-size: $text-body;
  font-weight: $font-weight-bold;
}

// Square, so the pane beside it takes whatever width is left.
.image-wrapper {
  position: relative;
  flex-shrink: 0;
  width: $staff-review-height;
}

.review-image {
  box-sizing: border-box;
  outline: 0;

  // Always present so taking focus cannot reflow the row.
  border: $focus-border-width solid transparent;

  &:focus {
    border-color: $color-pink;
  }
}
</style>
